// ** React Imports
import { React } from "react"
// ** Third Party Components
import { Controller, useForm } from "react-hook-form"
import { Col, Input, Form, Button, Label, Row, FormFeedback, Spinner } from "reactstrap"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import Flatpickr from "react-flatpickr"
// ** Utils
import { getISODateUTC, checkFieldValidity } from "@lib/util"
import { ANONYMOUS_CLIENT } from "@lib/constants"
import SelectShowroomListbox from "../../components/SelectShowroomListbox"
// ** Store & Actions
import { useSelector } from "react-redux"

const AnonymousEventForm = (props) => {
  const userStore = useSelector((state) => state.users)
  const today = getISODateUTC().split("Z")[0]
  const MySwal = withReactContent(Swal)
  // default form values
  const defaultValues = {
    description: "",
    currency: "",
    date: today
  }
  const {
    control,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty }
  } = useForm({ defaultValues })

  const onSubmit = async (data) => {
    if (
      !userStore.selectedUser.collaboratorId ||
      !userStore.selectedUser.showroomId ||
      !userStore.selectedUser.currencies
    ) {
      MySwal.fire({
        title: "Error",
        allowOutsideClick: false,
        html: `
        <div>
          <p>Your account is missing the following information :</p>
          ${!userStore.selectedUser.collaboratorId ? "<p>collaboratorId</p>" : ""}
          ${!userStore.selectedUser.showroomId ? "<p>showroomId</p>" : ""}
          ${!userStore.selectedUser.currencies ? "<p>currencies</p>" : ""}
        </div>`,
        icon: "error"
      })
      return
    }

    // cleanup data
    const cleanData = {
      ...(data.currency && { currencyID: data.currency }),
      collaboratorId: userStore.selectedUser.collaboratorId,
      showroomId: userStore.selectedUser.showroomId,
      description: data.description,
      interactionId: ANONYMOUS_CLIENT.interactionId,
      on: data.date.length === 1 ? getISODateUTC(new Date(data.date)) : today
    }

    let error = false
    const optionalFields = ["date", "showroomId"].concat(props.additionalOptionalFields)
    for (const key in data) {
      if (optionalFields.includes(key)) continue

      if (!checkFieldValidity(data[key])) {
        setError(key, {
          type: "manual",
          message: `'${key}' is required`
        })
        error = true
      }
    }

    if (!error) {
      try {
        // if all good, then post + redirect to config
        const postToConfigResponse = await props.onSubmit(cleanData)
        if (postToConfigResponse.orderID) {
          MySwal.fire({
            title: "Success",
            html: (
              <div className="overflow-hidden">
                <p>Redirecting to configurator...</p>
                <Spinner />
              </div>
            ),
            showConfirmButton: false,
            allowOutsideClick: false
          })
          window.location.href = `/configurator/orders/details/${postToConfigResponse.orderID}`
        }
      } catch (err) {
        console.error(err)
        MySwal.fire({
          title: "Error",
          text: err.message,
          icon: "error"
        })
      }
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3">
        <h1>{props.title}</h1>
        <h5>{props.subtitle}</h5>
      </div>
      <Row className="mb-1">
        <Col sm="3">
          <Label for="description">Description *</Label>
        </Col>
        <Col sm="9">
          <Controller
            id="description"
            name="description"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  type="textarea"
                  rows="4"
                  placeholder="Requests, visits, informations, etc..."
                  invalid={errors.description && true}
                />
                {errors.description && <FormFeedback>{errors.description.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row className="mb-1">
        <Label sm="3" for="currency">
          Currency *
        </Label>
        <Col sm="9">
          <Controller
            id="currency"
            name="currency"
            control={control}
            render={({ field }) => (
              <>
                <Input {...field} type="select">
                  <option value="">Select a currency</option>
                  {userStore.selectedUser.currencies?.map((currency) => (
                    <option key={currency.id} value={parseInt(currency.id)}>
                      {currency.iso3} - {currency.name}
                    </option>
                  ))}
                </Input>
                {errors.currency && (
                  <small style={{ color: "#ea5455" }}>{errors.currency.message}</small>
                )}
              </>
            )}
          />
        </Col>
      </Row>
      <Row className="mb-1">
        <Label sm="3" for="description">
          Event date
        </Label>
        <Col sm="9">
          <Controller
            name="date"
            id="date"
            control={control}
            render={({ field }) => (
              <Flatpickr
                {...field}
                className="form-control"
                placeholder={today}
                options={{
                  dateFormat: "Y-m-d H:i",
                  locale: {
                    firstDayOfWeek: 1
                  },
                  maxDate: today,
                  enableTime: true,
                  time_24hr: true
                }}
              />
            )}
          />
        </Col>
      </Row>

      <hr className="my-3" />

      <Row>
        <Col>
          <SelectShowroomListbox user={userStore.selectedUser} control={control} />
        </Col>
        <Col className="align-self-center text-end">
          <Button color="success" type="submit" disabled={!isDirty || isSubmitting}>
            {isSubmitting ? "Submitting..." : "Continue"}
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default AnonymousEventForm
